/**
 * /control/contingencia · Bitácora manual.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { IC_ESTADOS, fmtFecha, fmtMXN } from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Zap, Plus, Link2, AlertTriangle } from "lucide-react";

export function ContingenciaList() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [fids, setFids] = useState([]);
    const [form, setForm] = useState({
        fideicomiso_id: "", canal: "f07_fisico", tipo: "pago",
        causa: "sistema_externo_caido", instructor: "",
        monto: 0, cuenta_origen: "", cuenta_destino: "", concepto: "",
        autorizador_1: "", autorizador_2: "",
        evidencia_doc_id: "",
    });
    const nav = useNavigate();

    const load = () => api.get("/control/contingencia").then(r => setItems(r.data));
    useEffect(() => {
        load();
        api.get("/fideicomisos").then(r => {
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            setFids(arr.map(f => ({ id: f.id, label: f.numero_fideicomiso || f.id })));
        }).catch(() => setFids([]));
    }, []);

    const requiere2 = Number(form.monto) > 5_000_000;

    const crear = async () => {
        if (!form.fideicomiso_id || form.concepto.length < 5 || !form.autorizador_1
             || !form.evidencia_doc_id) {
            toast.error("Fideicomiso, concepto, 1 autorizador y evidencia requeridos"); return;
        }
        const autorizaciones = [{
            usuario_email: form.autorizador_1,
            rol: "operaciones",
        }];
        if (requiere2 && form.autorizador_2) {
            autorizaciones.push({
                usuario_email: form.autorizador_2, rol: "tesoreria",
            });
        }
        try {
            const r = await api.post("/control/contingencia", {
                fideicomiso_id: form.fideicomiso_id,
                fecha_evento: new Date().toISOString(),
                canal: form.canal, tipo: form.tipo,
                causa_contingencia: form.causa,
                instructor_nombre: form.instructor || "Operador",
                monto_mxn: Number(form.monto),
                cuenta_origen: form.cuenta_origen || null,
                cuenta_destino: form.cuenta_destino || null,
                concepto: form.concepto,
                autorizaciones,
                evidencia_contingencia_doc_id: form.evidencia_doc_id,
            });
            toast.success(`IC registrada · ${r.data.folio_contingencia}`);
            setOpen(false);
            nav(`/control/contingencia/${r.data.id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail
                : Array.isArray(detail) ? detail.map(d => d.msg).join(", ")
                : "Error");
        }
    };

    return (
        <div className="space-y-6" data-testid="contingencia-list">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Instrucciones de Contingencia
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Bitácora manual · doble autorización &gt; $5,000,000.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                            data-testid="btn-nueva-ic">
                            <Plus className="h-4 w-4 mr-1" /> Nueva instrucción
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Registrar instrucción de contingencia</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Fideicomiso</label>
                                    <Select value={form.fideicomiso_id}
                                        onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                        <SelectTrigger><SelectValue placeholder="..." /></SelectTrigger>
                                        <SelectContent>
                                            {fids.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Canal</label>
                                    <Select value={form.canal} onValueChange={v => setForm({ ...form, canal: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="f07_fisico">F-07 físico</SelectItem>
                                            <SelectItem value="correo_registrado">Correo registrado</SelectItem>
                                            <SelectItem value="oficio_fisico">Oficio físico</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Tipo</label>
                                    <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["pago", "inversion", "entrega", "poder", "otra"].map(t =>
                                                <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Causa</label>
                                    <Select value={form.causa} onValueChange={v => setForm({ ...form, causa: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["sistema_externo_caido", "banca_offline", "telecomunicaciones",
                                              "inhabil_operador", "otra"].map(c =>
                                                <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Instructor</label>
                                    <Input value={form.instructor}
                                        onChange={e => setForm({ ...form, instructor: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Monto MXN</label>
                                    <Input type="number" step="0.01" value={form.monto}
                                        onChange={e => setForm({ ...form, monto: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Cuenta origen</label>
                                    <Input value={form.cuenta_origen}
                                        onChange={e => setForm({ ...form, cuenta_origen: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Cuenta destino</label>
                                    <Input value={form.cuenta_destino}
                                        onChange={e => setForm({ ...form, cuenta_destino: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Concepto</label>
                                <Textarea rows={2} value={form.concepto}
                                    onChange={e => setForm({ ...form, concepto: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Doc. evidencia de contingencia (ID)</label>
                                <Input value={form.evidencia_doc_id}
                                    onChange={e => setForm({ ...form, evidencia_doc_id: e.target.value })} />
                            </div>
                            <div className={`p-3 rounded border ${requiere2 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                                <p className="text-xs font-semibold mb-2">
                                    {requiere2 ? "Monto > $5M · Requiere 2 autorizaciones distintas" : "Monto ≤ $5M · 1 autorización"}
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs">Autorizador 1 (email)</label>
                                        <Input value={form.autorizador_1}
                                            onChange={e => setForm({ ...form, autorizador_1: e.target.value })} />
                                    </div>
                                    {requiere2 && (
                                        <div>
                                            <label className="text-xs">Autorizador 2 (email, distinto)</label>
                                            <Input value={form.autorizador_2}
                                                onChange={e => setForm({ ...form, autorizador_2: e.target.value })} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                            <Button onClick={crear} data-testid="btn-crear-ic">Registrar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Fecha evento</th>
                                <th className="px-3 py-2 text-left">Tipo</th>
                                <th className="px-3 py-2 text-right">Monto</th>
                                <th className="px-3 py-2 text-center">2 firmas</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Sin IC.</td></tr>
                            )}
                            {items.map(ic => {
                                const cfg = IC_ESTADOS[ic.estado] || { label: ic.estado, cls: "" };
                                return (
                                    <tr key={ic.id} className="border-t hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <Link to={`/control/contingencia/${ic.id}`}
                                                className="text-primary hover:underline">
                                                {ic.folio_contingencia}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(ic.fecha_evento)}</td>
                                        <td className="px-3 py-2 text-xs">{ic.tipo}</td>
                                        <td className="px-3 py-2 text-right">{fmtMXN(ic.monto_mxn)}</td>
                                        <td className="px-3 py-2 text-center">
                                            {ic.requiere_doble_autorizacion ? "Sí" : "No"}
                                        </td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}


export function ContingenciaDetalle() {
    const { id } = useParams();
    const [ic, setIc] = useState(null);
    const reload = () => api.get(`/control/contingencia/${id}`).then(r => setIc(r.data));
    useEffect(() => { reload(); }, [id]);
    if (!ic) return <div className="p-6 text-muted-foreground">Cargando…</div>;
    const cfg = IC_ESTADOS[ic.estado] || { label: ic.estado, cls: "" };

    const conciliar = async () => {
        const insId = prompt("ID de la instrucción posterior en el sistema:");
        if (!insId) return;
        const monto = parseFloat(prompt("Monto verificado:", String(ic.monto_mxn)) || "0");
        if (isNaN(monto) || monto <= 0) return;
        try {
            await api.post(`/control/contingencia/${id}/conciliar`, {
                instruccion_sistema_posterior_id: insId,
                monto_verificado_mxn: monto,
                observaciones: "Conciliación capturada desde UI",
            });
            toast.success("Conciliada");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    return (
        <div className="space-y-6" data-testid="contingencia-detalle">
            <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                    {ic.folio_contingencia}
                </h1>
                <Badge className={cfg.cls}>{cfg.label}</Badge>
                {ic.requiere_doble_autorizacion && (
                    <Badge className="bg-sky-100 text-sky-900 border-sky-300 border">
                        Doble autorización
                    </Badge>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="md:col-span-2">
                    <CardHeader><CardTitle className="text-base">Detalle del evento</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-2">
                        <p><b>Fecha evento:</b> {fmtFecha(ic.fecha_evento)}</p>
                        <p><b>Canal:</b> {ic.canal} · <b>Tipo:</b> {ic.tipo}</p>
                        <p><b>Causa:</b> {ic.causa_contingencia}</p>
                        <p><b>Instructor:</b> {ic.instructor_nombre}</p>
                        <p><b>Monto:</b> {fmtMXN(ic.monto_mxn)}</p>
                        {(ic.cuenta_origen || ic.cuenta_destino) && (
                            <p className="text-xs">
                                {ic.cuenta_origen && <>Origen: <code>{ic.cuenta_origen}</code></>}
                                {ic.cuenta_destino && <> · Destino: <code>{ic.cuenta_destino}</code></>}
                            </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">{ic.concepto}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle className="text-base">Acciones</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {ic.estado === "registrada" && (
                            <Button className="w-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                onClick={conciliar} data-testid="btn-conciliar-ic">
                                <Link2 className="h-4 w-4 mr-1" /> Conciliar con sistema
                            </Button>
                        )}
                        {ic.estado === "observada" && (
                            <div className="p-2 rounded border border-amber-300 bg-amber-50 text-xs">
                                <AlertTriangle className="h-4 w-4 inline mr-1 text-amber-700" />
                                Delta de conciliación: {ic.delta_conciliacion_pct}%
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Autorizaciones</CardTitle></CardHeader>
                <CardContent className="p-0">
                    <table className="w-full text-sm" data-testid="tabla-autorizaciones">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Usuario</th>
                                <th className="px-3 py-2 text-left">Rol</th>
                                <th className="px-3 py-2 text-left">Fecha</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(ic.autorizaciones || []).map((a, i) => (
                                <tr key={i} className="border-t">
                                    <td className="px-3 py-2 text-xs">{a.usuario_email}</td>
                                    <td className="px-3 py-2 text-xs">{a.rol}</td>
                                    <td className="px-3 py-2 text-xs">{fmtFecha(a.fecha)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
            {ic.instruccion_sistema_posterior_id && (
                <p className="text-xs text-muted-foreground">
                    Conciliada contra: <code>{ic.instruccion_sistema_posterior_id}</code> ·
                    Fecha: {fmtFecha(ic.fecha_conciliacion)}
                </p>
            )}
        </div>
    );
}

export default ContingenciaList;
